<template>
  <el-dialog
    :visible.sync="visible"
    title="编辑2D平面图"
    width="90%"
    top="2vh"
    custom-class="custom-modal"
    @close="handleClose"
  >
    <div id="app" class="app-container">
      <!-- 房间列表区域 -->
      <div class="top-room-list">
        <div class="panel">
          <h3 class="panel-title"><i class="fa fa-home mr-2"></i>房间列表</h3>
          <div class="room-list-container horizontal">
            <div
              v-if="roomList && roomList.length > 0"
              class="room-list horizontal"
            >
              <div
                v-for="(room, index) in roomList"
                :key="index"
                class="room-item horizontal"
              >
                <div class="room-info">
                  <div class="room-name">
                    {{ room.roomName || room.roomNum || "未命名房间" }}
                  </div>
                  <div class="room-details">
                    <span class="room-type">{{
                      room.roomSubTypeName || "未分类"
                    }}</span>
                    <span
                      :class="['room-status', getStatusClass(room.state)]"
                      >{{ room.stateName || "未知状态" }}</span
                    >
                  </div>
                  <div class="room-extra">
                    <span class="room-area">{{ room.roomArea || "0" }}m²</span>
                    <span class="room-owner" v-if="room.ownerName">{{
                      room.ownerName
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="no-rooms">
              <i class="fa fa-info-circle"></i>
              <p>暂无房间信息</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 内容区域 -->
      <main class="main-content">
        <!-- 左侧工具栏 -->
        <aside class="sidebar">
          <div class="panel">
            <h3 class="panel-title">
              <i class="fa fa-archive mr-2"></i>工具栏
            </h3>

            <!-- 基础图形 -->
            <section class="tool-section">
              <h4 class="section-title">基础图形</h4>
              <div class="tool-grid">
                <button
                  class="tool-btn custom-btn custom-btn-sm custom-btn-primary"
                  @click="addRectangle"
                >
                  <i class="fa fa-square mr-2"></i> 矩形
                </button>
                <button
                  class="tool-btn custom-btn custom-btn-sm custom-btn-primary"
                  @click="addCircle"
                >
                  <i class="fa fa-circle mr-2"></i> 圆形
                </button>
                <button
                  class="tool-btn custom-btn custom-btn-sm custom-btn-primary"
                  @click="addTriangle"
                >
                  <i class="fa fa-play mr-2"></i> 三角形
                </button>
                <button
                  class="tool-btn custom-btn custom-btn-sm custom-btn-primary"
                  @click="addText"
                >
                  <i class="fa fa-font mr-2"></i> 文本
                </button>
              </div>
            </section>

            <!-- 绘制模式 -->
            <section class="tool-section">
              <h4 class="section-title">绘制模式</h4>
              <button
                :class="[
                  'tool-btn',
                  'custom-btn',
                  'custom-btn-sm',
                  'full-width',
                  'margin-bottom-2',
                  drawingMode ? 'custom-btn-secondary' : 'custom-btn-success',
                ]"
                @click="toggleDrawingMode"
              >
                <i class="fa fa-pencil-square mr-2"></i>
                {{ drawingMode ? "禁用绘制" : "启用绘制" }}
              </button>
              <div class="flex-container gap-2">
                <input type="color" v-model="brushColor" class="color-picker" />
                <input
                  type="range"
                  v-model.number="brushWidth"
                  min="1"
                  max="20"
                  class="range-slider"
                />
              </div>
            </section>

            <!-- 区域绘制 -->
            <section class="tool-section">
              <h4 class="section-title">区域绘制</h4>
              <button
                :class="[
                  'tool-btn',
                  'custom-btn',
                  'custom-btn-sm',
                  'full-width',
                  'margin-bottom-2',
                  polygonMode ? 'custom-btn-error' : 'custom-btn-warning',
                ]"
                @click="togglePolygonMode"
              >
                <i class="fa fa-pencil mr-2"></i>
                {{ polygonMode ? "完成区域绘制" : "开始绘制区域" }}
              </button>
              <div v-if="polygonMode" class="polygon-info">
                <p class="info-text">
                  点击画布添加顶点，双击或点击"完成区域绘制"按钮结束
                </p>
                <button
                  class="tool-btn custom-btn custom-btn-sm custom-btn-error full-width margin-top-2"
                  @click="cancelPolygonDrawing"
                >
                  <i class="fa fa-repeat mr-2"></i> 取消绘制
                </button>
              </div>
            </section>

            <!-- 选中对象属性 -->
            <section v-if="hasSelectedObjects && isSingleSelected" class="tool-section">
              <h4 class="section-title">
                <i class="fa fa-paint-brush mr-2"></i>选中对象属性
                <span class="selected-type-tag">{{ selectedType }}</span>
              </h4>

              <!-- 非文本对象才显示填充色 -->
              <div v-if="!isTextSelected" class="prop-row">
                <label class="prop-label">填充颜色</label>
                <input
                  type="color"
                  v-model="selectedFill"
                  @input="applyFillColor"
                  class="color-picker"
                />
              </div>

              <div class="prop-row">
                <label class="prop-label">边框颜色</label>
                <input
                  type="color"
                  v-model="selectedStroke"
                  @input="applyStrokeColor"
                  class="color-picker"
                />
              </div>

              <div class="prop-row">
                <label class="prop-label">边框宽度</label>
                <input
                  type="range"
                  v-model.number="selectedStrokeWidth"
                  min="0"
                  max="20"
                  @input="applyStrokeWidth"
                  class="range-slider"
                />
                <span class="prop-value">{{ selectedStrokeWidth }}</span>
              </div>

              <!-- 文本对象特有属性 -->
              <template v-if="isTextSelected">
                <div class="prop-row">
                  <label class="prop-label">文字内容</label>
                  <input
                    type="text"
                    v-model="selectedText"
                    @change="applyText"
                    class="text-input"
                    placeholder="输入文字"
                  />
                </div>
                <div class="prop-row">
                  <label class="prop-label">字体大小</label>
                  <input
                    type="number"
                    v-model.number="selectedFontSize"
                    min="8"
                    max="200"
                    @change="applyFontSize"
                    class="text-input"
                  />
                </div>
                <div class="prop-row">
                  <label class="prop-label">文字颜色</label>
                  <input
                    type="color"
                    v-model="selectedTextColor"
                    @input="applyTextColor"
                    class="color-picker"
                  />
                </div>
              </template>
            </section>

            <!-- 操作控制 -->
            <section class="tool-section">
              <h4 class="section-title">操作控制</h4>
              <button
                class="tool-btn custom-btn custom-btn-sm custom-btn-warning full-width margin-bottom-2"
                @click="undo"
                :disabled="!canUndo"
              >
                <i class="fa fa-undo mr-2"></i> 撤销
              </button>
              <button
                class="tool-btn custom-btn custom-btn-sm custom-btn-warning full-width margin-bottom-2"
                @click="redo"
                :disabled="!canRedo"
              >
                <i class="fa fa-repeat mr-2"></i> 重做
              </button>
              <button
                class="tool-btn custom-btn custom-btn-sm custom-btn-error full-width margin-bottom-2"
                @click="deleteSelected"
                :disabled="!hasSelection"
              >
                <i class="fa fa-times mr-2"></i> 删除选中
              </button>
              <button
                class="tool-btn custom-btn custom-btn-sm custom-btn-error full-width margin-bottom-2"
                @click="clearCanvas"
              >
                <i class="fa fa-trash mr-2"></i> 清空画布
              </button>
              <button
                class="tool-btn custom-btn custom-btn-sm custom-btn-info full-width margin-bottom-2"
                @click="saveCanvasAsImage"
              >
                <i class="fa fa-image mr-2"></i> 保存为图片
              </button>
            </section>
          </div>
        </aside>

        <!-- 画布区域 -->
        <section class="canvas-area">
          <div class="canvas-panel">
            <div class="canvas-container">
              <canvas ref="canvas" width="1200" height="600"></canvas>
            </div>
          </div>
        </section>
      </main>
    </div>
    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取 消</el-button>
      <el-button type="primary" @click="handleSave">保 存</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { fabric } from "fabric";
import { addCanvasData } from "@/api/room/roomApi";

export default {
  name: "App",
  data() {
    return {
      visible: false,
      canvas: null,
      drawingMode: false,
      brushColor: "#000000",
      brushWidth: 5,
      // 多边形绘制相关状态
      polygonMode: false,
      isDrawingPolygon: false,
      polygonPoints: [],
      tempPolygon: null,
      polygonLines: [],
      previewLine: null, // 添加预览线变量
      currentData: null,
      currentUnitData: null,
      communityId: "",
      roomList: [], // 房间列表数据
      // 撤销/重做相关
      historyStack: [], // 历史记录栈
      historyIndex: -1, // 当前历史记录索引
      isUndoRedoing: false, // 标记是否正在执行撤销/重做操作
      hasSelectedObjects: false, // 是否有选中的对象
      // 选中对象属性面板
      selectedFill: "#3b82f6",
      selectedStroke: "#000000",
      selectedStrokeWidth: 1,
      selectedText: "",
      selectedFontSize: 24,
      selectedTextColor: "#000000",
      selectedType: "",
      isTextSelected: false,
      isSingleSelected: false,
    };
  },
  computed: {
    canUndo() {
      return this.historyIndex > 0;
    },
    canRedo() {
      return this.historyIndex < this.historyStack.length - 1;
    },
    hasSelection() {
      return this.hasSelectedObjects;
    },
  },

  mounted() {
    // 添加键盘快捷键监听
    document.addEventListener("keydown", this.handleKeyDown);
  },
  beforeDestroy() {
    // 移除键盘快捷键监听
    document.removeEventListener("keydown", this.handleKeyDown);
  },
  methods: {
    open(params) {
      this.visible = true;

      this.currentData = { ...params.currentData };
      this.currentUnitData = { ...params.currentUnitData };
      this.communityId = params.communityId;

      // 初始化房间列表数据
      this.roomList = this.currentData.roomDtoList || [];
      console.log("房间列表数据:", this.roomList);

      this.$nextTick(() => {
        if (!this.canvas) {
          this.initCanvas();
        } else if (
          this.currentData.floorUnitDto &&
          this.currentData.floorUnitDto.canvas
        ) {
          // 如果画布已初始化且有数据，直接加载数据
          this.loadCanvasData(this.currentData.floorUnitDto.canvas);
        }
      });
    },

    handleClose() {
      this.visible = false;
      if (this.canvas) {
        this.canvas.clear();
        this.canvas.backgroundColor = "#f9fafb";
        this.canvas.renderAll();
      }
      // 重置历史记录
      this.historyStack = [];
      this.historyIndex = -1;
      // 重置选中状态
      this.hasSelectedObjects = false;
    },
    initCanvas() {
      const canvasElement = this.$refs.canvas;

      if (!canvasElement) {
        console.error("找不到画布元素");
        return;
      }

      this.canvas = new fabric.Canvas(canvasElement, {
        backgroundColor: "#f9fafb",
        selection: true,
      });

      // 有canvas数据，则直接加载
      if (
        this.currentData.floorUnitDto &&
        this.currentData.floorUnitDto.canvas
      ) {
        this.loadCanvasData(this.currentData.floorUnitDto.canvas);
      }

      // 初始化历史记录
      this.saveCanvasState();

      // 监听画布变化事件
      this.setupCanvasListeners();

      // 监听选中状态变化，用于更新删除按钮状态和属性面板
      this.canvas.on("selection:created", () => {
        this.hasSelectedObjects = true;
        this.loadSelectedProperties();
      });
      this.canvas.on("selection:updated", () => {
        this.hasSelectedObjects = true;
        this.loadSelectedProperties();
      });
      this.canvas.on("selection:cleared", () => {
        this.hasSelectedObjects = false;
        this.isSingleSelected = false;
        this.isTextSelected = false;
      });

      // 对象被修改（拖动、缩放后）同步属性面板
      this.canvas.on("object:modified", () => {
        this.loadSelectedProperties();
      });

      // 文字对象移动后自动置顶，确保不被图形遮挡
      this.canvas.on("object:moving", (e) => {
        const target = e.target;
        if (
          target &&
          (target.type === "i-text" ||
            target.type === "text" ||
            target.type === "textbox")
        ) {
          target.bringToFront();
        }
      });

      // 文本编辑，双击进入编辑状态
      this.canvas.on("mouse:dblclick", (e) => {
        const target = e.target;

        if (this.isDrawingPolygon) return;

        if (target && target.type === "i-text") {
          target.enterEditing();
        }
      });
    },

    // 设置画布监听器
    setupCanvasListeners() {
      if (!this.canvas) return;

      // 监听对象添加
      this.canvas.on("object:added", () => {
        // 如果正在绘制多边形，不保存历史记录
        if (!this.isUndoRedoing && !this.isDrawingPolygon) {
          this.saveCanvasState();
        }
      });

      // 监听对象修改
      this.canvas.on("object:modified", () => {
        if (!this.isUndoRedoing && !this.isDrawingPolygon) {
          this.saveCanvasState();
        }
      });

      // 监听对象移除
      this.canvas.on("object:removed", () => {
        if (!this.isUndoRedoing && !this.isDrawingPolygon) {
          this.saveCanvasState();
        }
      });
    },

    // 保存画布状态到历史记录
    saveCanvasState() {
      if (!this.canvas || this.isUndoRedoing) return;

      const canvasState = JSON.stringify(this.canvas.toJSON());

      // 如果当前不在历史记录的末尾，删除后面的记录
      if (this.historyIndex < this.historyStack.length - 1) {
        this.historyStack = this.historyStack.slice(0, this.historyIndex + 1);
      }

      // 添加新状态
      this.historyStack.push(canvasState);
      this.historyIndex++;

      // 历史记录数量
      const maxHistorySize = 100;
      if (this.historyStack.length > maxHistorySize) {
        this.historyStack.shift();
        this.historyIndex--;
      }
    },

    // 撤销操作
    undo() {
      if (!this.canUndo || !this.canvas) return;

      this.isUndoRedoing = true;
      this.historyIndex--;

      const canvasState = this.historyStack[this.historyIndex];
      this.canvas.loadFromJSON(canvasState, () => {
        this.canvas.renderAll();
        this.isUndoRedoing = false;
      });
    },

    // 重做操作
    redo() {
      if (!this.canRedo || !this.canvas) return;

      this.isUndoRedoing = true;
      this.historyIndex++;

      const canvasState = this.historyStack[this.historyIndex];
      this.canvas.loadFromJSON(canvasState, () => {
        this.canvas.renderAll();
        this.isUndoRedoing = false;
      });
    },

    loadCanvasData(data) {
      if (!this.canvas || !data) return;

      this.canvas.loadFromJSON(data, () => {
        this.canvas.renderAll();
      });
    },

    // 获取canvas中所有文本数据
    getAllTextFromCanvas() {
      if (!this.canvas) return [];

      const textArray = [];
      const allObjects = this.canvas.getObjects();

      // 递归查找对象中的文本
      const findTextInObject = (obj) => {
        // 文本对象
        if (
          obj.type === "text" ||
          obj.type === "i-text" ||
          obj.type === "textbox"
        ) {
          return obj.text;
        }
        // 组对象
        else if (obj.type === "group" && obj.objects) {
          for (let i = 0; i < obj.objects.length; i++) {
            const text = findTextInObject(obj.objects[i]);
            if (text) return text;
          }
        }
        // 其他对象
        else if (obj._objects && obj._objects.length > 0) {
          for (let i = 0; i < obj._objects.length; i++) {
            const text = findTextInObject(obj._objects[i]);
            if (text) return text;
          }
        }

        // 检查对象属性中的文本
        for (const prop in obj) {
          if (obj[prop] && typeof obj[prop] === "object" && obj[prop].text) {
            return obj[prop].text;
          }
        }

        return "";
      };

      // 遍历所有对象，查找文本
      for (let i = 0; i < allObjects.length; i++) {
        const obj = allObjects[i];
        const text = findTextInObject(obj);
        if (text && text.trim() !== "") {
          textArray.push(text.trim());
        }
      }

      return textArray;
    },

    // 根据房间号查找房间状态
    getRoomStatusByRoomNumber(roomNumber) {
      if (!this.roomList || this.roomList.length === 0) return null;

      const room = this.roomList.find((room) => room.roomNum === roomNumber);
      return room
        ? {
            state: room.state,
            stateName: room.stateName,
            roomSubTypeName: room.roomSubTypeName,
          }
        : null;
    },

    // 根据房间状态获取对应的颜色
    getColorByRoomStatus(state) {
      const statusColors = {
        2001: "#fee2e2", // 租赁中
        2002: "#f3e8ff", // 未销售
        2003: "#e0f2fe", // 已交房
        2004: "#fefce8", // 未入住
        2005: "#ecfdf5", // 已装修
        2007: "#fee2e2", // 已售
        2008: "#f0f9ff", // 空置中
        2009: "#fef3c7", // 装修中
      };

      return statusColors[state] || "#f3f4f6"; // 默认颜色 - 浅灰色
    },

    // 更新画布中房间区域的背景颜色
    updateRoomAreaColors() {
      if (!this.canvas) return;

      const allObjects = this.canvas.getObjects();

      // 遍历所有对象，查找包含房间号的文本
      allObjects.forEach((obj) => {
        if (
          obj.type === "text" ||
          obj.type === "i-text" ||
          obj.type === "textbox"
        ) {
          const roomNumber = obj.text ? obj.text.trim() : "";

          if (roomNumber && /^\d+$/.test(roomNumber)) {
            // 只处理纯数字的房间号
            const roomStatus = this.getRoomStatusByRoomNumber(roomNumber);

            if (roomStatus) {
              // 查找这个文本对象附近的矩形或多边形区域
              const nearbyArea = this.findNearbyArea(obj);

              if (nearbyArea) {
                // 更新区域的背景颜色和边框颜色
                const newColor = this.getColorByRoomStatus(roomStatus.state);

                if (nearbyArea.type === "rect") {
                  nearbyArea.set("fill", newColor);
                  nearbyArea.set("stroke", newColor);
                } else if (nearbyArea.type === "polygon") {
                  nearbyArea.set("fill", newColor);
                  nearbyArea.set("stroke", newColor);
                }
              }
            }
          }
        }
      });

      this.canvas.renderAll();
    },

    // 查找文本对象附近的区域（矩形或多边形）
    findNearbyArea(textObj) {
      if (!textObj || !this.canvas) return null;

      const allObjects = this.canvas.getObjects();
      const textBounds = textObj.getBoundingRect();

      // 定义搜索范围（可以根据需要调整）
      const searchRadius = 100;

      // 查找附近的矩形或多边形
      for (let i = allObjects.length - 1; i >= 0; i--) {
        const obj = allObjects[i];

        // 跳过文本对象本身
        if (obj === textObj) continue;

        // 查找矩形或多边形
        if (obj.type === "rect" || obj.type === "polygon") {
          const objBounds = obj.getBoundingRect();

          // 检查是否在搜索范围内
          const distance = this.calculateDistance(textBounds, objBounds);

          if (distance < searchRadius) {
            return obj;
          }
        }
      }

      return null;
    },

    // 计算两个边界框之间的距离
    calculateDistance(bounds1, bounds2) {
      const center1 = {
        x: bounds1.left + bounds1.width / 2,
        y: bounds1.top + bounds1.height / 2,
      };

      const center2 = {
        x: bounds2.left + bounds2.width / 2,
        y: bounds2.top + bounds2.height / 2,
      };

      return Math.sqrt(
        Math.pow(center2.x - center1.x, 2) + Math.pow(center2.y - center1.y, 2),
      );
    },
    // 保存画布数据
    handleSave() {
      // 在保存前，先更新房间区域的背景颜色
      this.updateRoomAreaColors();

      // 获取当前画布的JSON数据
      const canvasJSON = JSON.stringify(this.canvas.toJSON());

      // 获取图片的Base64数据
      this.getCanvasAsBase64((imageData) => {
        // 获取当前canvas所有文本数据 组合楼栋-单元编号-房间号
        const roomNumber = this.getAllTextFromCanvas().map(
          (item) =>
            this.currentUnitData.parentFloorNum +
            "-" +
            this.currentUnitData.unitNum +
            "-" +
            item,
        );

        addCanvasData({
          id:
            this.currentData &&
            this.currentData.floorUnitDto &&
            this.currentData.floorUnitDto.id
              ? this.currentData.floorUnitDto.id
              : null,
          canvas: canvasJSON,
          floorPlan: imageData,
          floorId: this.currentUnitData.parentFloorId, //楼ID
          floorNum: this.currentUnitData.parentFloorNum, //楼编号
          name: this.currentUnitData.parentFloorName, //小区楼名称
          communityId: this.communityId, //小区ID
          unitId: this.currentUnitData.unitId,
          unitNum: this.currentUnitData.unitNum,
          remark: "",
          roomNumber,
          roomDtoList: this.currentData.roomDtoList || [],
        }).then((res) => {
          if (res.code == 0) {
            this.visible = false;

            this.$message({
              message: "保存成功",
              type: "success",
            });

            // 通知父组件刷新数据
            this.$emit("refresh");
          } else {
            this.$message({
              message: "保存失败",
              type: "error",
            });
          }
        });
      });
    },

    // 获取canvas的Base64图片数据
    getCanvasAsBase64(callback) {
      if (!this.canvas) {
        console.error("画布未初始化");
        return;
      }

      // 创建一个临时canvas
      const tempCanvas = document.createElement("canvas");
      const tempCtx = tempCanvas.getContext("2d");
      tempCanvas.width = this.canvas.width;
      tempCanvas.height = this.canvas.height;

      tempCtx.fillStyle = "#ffffff";
      tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

      // 将fabric canvas的内容绘制到临时canvas上
      const dataURL = this.canvas.toDataURL({
        format: "png",
        quality: 1,
        multiplier: 1,
      });

      // 创建图片元素并绘制到临时canvas
      const img = new Image();
      img.onload = () => {
        tempCtx.drawImage(img, 0, 0);

        // 将临时canvas转换为Base64数据
        const finalDataURL = tempCanvas.toDataURL("image/png");
        callback(finalDataURL);
      };
      img.src = dataURL;
    },

    // 将canvas保存为图片
    saveCanvasAsImage() {
      this.getCanvasAsBase64((imageData) => {
        // 创建下载链接
        const link = document.createElement("a");
        link.download = "room-layout-" + new Date().getTime() + ".png";
        link.href = imageData;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.$message({
          message: "图片已保存",
          type: "success",
        });
      });
    },

    addRectangle() {
      console.log("添加矩形按钮被点击");
      console.log("画布状态:", this.canvas);

      if (!this.canvas) {
        console.error("画布未初始化");
        return;
      }

      const rect = new fabric.Rect({
        left: 100,
        top: 100,
        width: 100,
        height: 60,
        fill: "#3b82f6",
        stroke: "#1e40af",
        strokeWidth: 2,
        rx: 5,
        ry: 5,
      });

      console.log("创建的矩形对象:", rect);
      this.canvas.add(rect);
      this.canvas.setActiveObject(rect);
      this.canvas.renderAll();
      console.log("矩形已添加到画布");
    },

    addCircle() {
      if (!this.canvas) {
        console.error("画布未初始化");
        return;
      }
      const circle = new fabric.Circle({
        left: 150,
        top: 150,
        radius: 50,
        fill: "#ef4444",
        stroke: "#dc2626",
        strokeWidth: 2,
      });
      this.canvas.add(circle);
      this.canvas.setActiveObject(circle);
    },

    addTriangle() {
      if (!this.canvas) {
        console.error("画布未初始化");
        return;
      }
      const triangle = new fabric.Triangle({
        left: 200,
        top: 200,
        width: 80,
        height: 80,
        fill: "#10b981",
        stroke: "#059669",
        strokeWidth: 2,
      });
      this.canvas.add(triangle);
      this.canvas.setActiveObject(triangle);
    },

    addText() {
      if (!this.canvas) {
        console.error("画布未初始化");
        return;
      }
      const text = new fabric.IText("编辑房间号", {
        left: 250,
        top: 250,
        fontSize: 24,
        fontFamily: "Arial",
        fill: "#1f2937",
        fontWeight: "bold",
        editable: true,
      });

      // 添加文本输入限制，只允许输入数字
      text.on("editing:entered", () => {
        const hiddenTextarea = text.hiddenTextarea;
        if (hiddenTextarea) {
          // 监听输入事件，只允许数字
          hiddenTextarea.addEventListener("input", (e) => {
            // 只保留数字字符
            const numericValue = e.target.value.replace(/[^\d]/g, "");
            if (e.target.value !== numericValue) {
              e.target.value = numericValue;
              // 更新文本内容
              text.text = numericValue;
              text.canvas.renderAll();
            }
          });

          // 监听键盘事件，过滤非数字键
          hiddenTextarea.addEventListener("keydown", (e) => {
            const allowedKeys = [
              "Backspace",
              "Delete",
              "Tab",
              "Escape",
              "Enter",
              "ArrowLeft",
              "ArrowRight",
              "ArrowUp",
              "ArrowDown",
              "Home",
              "End",
            ];

            if (allowedKeys.includes(e.key)) {
              return;
            }

            if (e.ctrlKey || e.metaKey) {
              return;
            }

            // 非数字键，阻止输入
            if (!/^\d$/.test(e.key)) {
              e.preventDefault();
            }
          });

          // 监听粘贴事件，过滤非数字内容
          hiddenTextarea.addEventListener("paste", (e) => {
            e.preventDefault();
            const pastedText = (
              e.clipboardData || window.clipboardData
            ).getData("text");
            const numericText = pastedText.replace(/[^\d]/g, "");

            // 获取当前光标位置和选中文本
            const start = hiddenTextarea.selectionStart;
            const end = hiddenTextarea.selectionEnd;
            const currentValue = hiddenTextarea.value;

            // 插入过滤后的数字
            const newValue =
              currentValue.substring(0, start) +
              numericText +
              currentValue.substring(end);
            hiddenTextarea.value = newValue;

            // 更新文本内容
            text.text = newValue;
            text.canvas.renderAll();

            // 恢复光标位置
            setTimeout(() => {
              hiddenTextarea.selectionStart = hiddenTextarea.selectionEnd =
                start + numericText.length;
            }, 0);
          });
        }
      });

      this.canvas.add(text);
      text.bringToFront();
      this.canvas.setActiveObject(text);
      text.enterEditing();
    },

    toggleDrawingMode() {
      if (!this.canvas) {
        console.error("画布未初始化");
        return;
      }

      this.drawingMode = !this.drawingMode;
      this.canvas.isDrawingMode = this.drawingMode;

      if (this.drawingMode) {
        this.canvas.freeDrawingBrush.color = this.brushColor;
        this.canvas.freeDrawingBrush.width = this.brushWidth;
      }
    },

    // 多边形绘制相关方法
    togglePolygonMode() {
      if (!this.canvas) {
        console.error("画布未初始化");
        return;
      }

      if (this.polygonMode) {
        // 当前是绘制模式，尝试完成多边形
        this.finishPolygonDrawing();
      } else {
        // 当前不是绘制模式，开始绘制
        this.polygonMode = true;
        this.isDrawingPolygon = true;
        this.polygonPoints = [];
        this.setupPolygonDrawing();
      }
    },

    setupPolygonDrawing() {
      if (!this.canvas) {
        console.error("画布未初始化");
        return;
      }

      // 禁用画布的选择功能
      this.canvas.selection = false;

      // 移除之前的事件监听器
      this.canvas.off("mouse:down", this.handlePolygonClick);
      this.canvas.off("mouse:dblclick", this.handlePolygonDoubleClick);
      this.canvas.off("mouse:move", this.handlePolygonMouseMove);

      // 添加新的事件监听器
      this.canvas.on("mouse:down", this.handlePolygonClick);
      this.canvas.on("mouse:dblclick", this.handlePolygonDoubleClick);
      this.canvas.on("mouse:move", this.handlePolygonMouseMove);
    },

    handlePolygonClick(e) {
      if (!this.isDrawingPolygon || !this.canvas) return;

      const pointer = this.canvas.getPointer(e.e);
      this.polygonPoints.push({ x: pointer.x, y: pointer.y });

      // 临时禁用历史记录保存
      const wasUndoRedoing = this.isUndoRedoing;
      this.isUndoRedoing = true;

      // 如果有至少两个点，绘制线段
      if (this.polygonPoints.length >= 2) {
        const lastPoint = this.polygonPoints[this.polygonPoints.length - 2];
        const currentPoint = this.polygonPoints[this.polygonPoints.length - 1];

        const line = new fabric.Line(
          [lastPoint.x, lastPoint.y, currentPoint.x, currentPoint.y],
          {
            stroke: "#ff0000",
            strokeWidth: 2,
            selectable: false,
            evented: false,
          },
        );

        this.polygonLines.push(line);
        this.canvas.add(line);
      }

      // 添加点标记
      const circle = new fabric.Circle({
        left: pointer.x - 5,
        top: pointer.y - 5,
        radius: 5,
        fill: "#ff0000",
        selectable: false,
        evented: false,
      });

      this.polygonLines.push(circle);
      this.canvas.add(circle);

      // 恢复历史记录保存状态
      this.isUndoRedoing = wasUndoRedoing;
    },

    // 处理鼠标移动事件，显示预览线
    handlePolygonMouseMove(e) {
      if (
        !this.isDrawingPolygon ||
        !this.canvas ||
        this.polygonPoints.length === 0
      )
        return;

      const pointer = this.canvas.getPointer(e.e);
      const lastPoint = this.polygonPoints[this.polygonPoints.length - 1];

      // 临时禁用历史记录保存
      const wasUndoRedoing = this.isUndoRedoing;
      this.isUndoRedoing = true;

      if (this.previewLine) {
        this.canvas.remove(this.previewLine);
      }

      // 创建新的预览线
      this.previewLine = new fabric.Line(
        [lastPoint.x, lastPoint.y, pointer.x, pointer.y],
        {
          stroke: "#ff0000",
          strokeWidth: 2,
          selectable: false,
          evented: false,
          strokeDashArray: [5, 5], // 虚线样式
        },
      );

      this.canvas.add(this.previewLine);
      this.canvas.renderAll();

      // 恢复历史记录保存状态
      this.isUndoRedoing = wasUndoRedoing;
    },

    handlePolygonDoubleClick(e) {
      if (!this.isDrawingPolygon) return;
      e.e.preventDefault();
      e.e.stopPropagation();
      this.finishPolygonDrawing();
    },

    finishPolygonDrawing() {
      if (!this.canvas) {
        console.error("画布未初始化");
        return;
      }

      if (this.polygonPoints.length < 3) {
        alert("至少需要3个点才能形成多边形");
        // 清理临时绘制的内容
        this.cancelPolygonDrawing();
        return;
      }

      // 移除临时线条和点
      this.polygonLines.forEach((line) => {
        this.canvas.remove(line);
      });
      this.polygonLines = [];

      // 移除预览线
      if (this.previewLine) {
        this.canvas.remove(this.previewLine);
        this.previewLine = null;
      }

      // 创建多边形
      const polygon = new fabric.Polygon(this.polygonPoints, {
        fill: "rgba(0, 0, 255, 0.3)",
        stroke: "#0000ff",
        strokeWidth: 2,
        selectable: true,
      });

      // 重置状态（在添加多边形之前）
      this.isDrawingPolygon = false;
      this.polygonPoints = [];
      this.polygonMode = false;

      // 恢复画布的选择功能
      this.canvas.selection = true;

      // 移除事件监听器
      this.canvas.off("mouse:down", this.handlePolygonClick);
      this.canvas.off("mouse:dblclick", this.handlePolygonDoubleClick);
      this.canvas.off("mouse:move", this.handlePolygonMouseMove);

      // 添加多边形（此时 isDrawingPolygon 已经是 false，会触发历史记录保存）
      this.canvas.add(polygon);
      this.canvas.setActiveObject(polygon);
      this.canvas.renderAll();
    },

    cancelPolygonDrawing() {
      if (!this.canvas) {
        console.error("画布未初始化");
        return;
      }

      // 临时禁用历史记录保存
      const wasUndoRedoing = this.isUndoRedoing;
      this.isUndoRedoing = true;

      // 移除临时线条和点
      this.polygonLines.forEach((line) => {
        this.canvas.remove(line);
      });
      this.polygonLines = [];

      // 移除预览线
      if (this.previewLine) {
        this.canvas.remove(this.previewLine);
        this.previewLine = null;
      }

      // 恢复历史记录保存状态
      this.isUndoRedoing = wasUndoRedoing;

      // 重置状态
      this.isDrawingPolygon = false;
      this.polygonPoints = [];
      this.polygonMode = false;

      // 恢复画布的选择功能
      this.canvas.selection = true;

      // 移除事件监听器
      this.canvas.off("mouse:down", this.handlePolygonClick);
      this.canvas.off("mouse:dblclick", this.handlePolygonDoubleClick);
      this.canvas.off("mouse:move", this.handlePolygonMouseMove);

      this.canvas.renderAll();
    },

    clearCanvas() {
      if (!this.canvas) {
        console.error("画布未初始化");
        return;
      }
      if (confirm("确定要清空画布吗？")) {
        this.canvas.clear();
        this.canvas.backgroundColor = "#f9fafb";
        this.canvas.renderAll();
        // 清空后保存状态
        this.saveCanvasState();
      }
    },

    // 根据房间状态返回样式类
    getStatusClass(state) {
      if (!state) return "status-unknown";

      // 根据状态码返回对应的样式类
      switch (state) {
        case "2001":
          return "status-rented"; // 租赁中
        case "2002":
          return "status-unsold"; // 未销售
        case "2003":
          return "status-delivered"; // 已交房
        case "2004":
          return "status-unoccupied"; // 未入住
        case "2005":
          return "status-decorated"; // 已装修
        case "2007":
          return "status-sold"; // 已售
        case "2008":
          return "status-vacant"; // 空置中
        case "2009":
          return "status-decorating"; // 装修中
        default:
          return "status-unknown"; // 未知状态
      }
    },

    // 键盘快捷键处理
    handleKeyDown(e) {
      // 只在对话框打开时响应快捷键
      if (!this.visible) return;

      // 如果正在编辑文本，不响应删除键
      const activeObject = this.canvas ? this.canvas.getActiveObject() : null;
      if (activeObject && activeObject.isEditing) {
        return;
      }

      // Delete 或 Backspace: 删除选中对象
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        this.deleteSelected();
      }
      // Ctrl+Z 或 Cmd+Z: 撤销
      else if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        this.undo();
      }
      // Ctrl+Y 或 Cmd+Shift+Z: 重做
      else if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === "y" || (e.key === "z" && e.shiftKey))
      ) {
        e.preventDefault();
        this.redo();
      }
    },

    // 删除选中的对象
    deleteSelected() {
      if (!this.canvas) {
        console.error("画布未初始化");
        return;
      }

      const activeObjects = this.canvas.getActiveObjects();

      if (activeObjects && activeObjects.length > 0) {
        // 删除所有选中的对象
        activeObjects.forEach((obj) => {
          this.canvas.remove(obj);
        });

        // 清除选中状态
        this.canvas.discardActiveObject();
        this.canvas.renderAll();

        // 更新选中状态
        this.hasSelectedObjects = false;
      } else {
        const activeObject = this.canvas.getActiveObject();
        if (activeObject) {
          this.canvas.remove(activeObject);
          this.canvas.renderAll();
          this.hasSelectedObjects = false;
        }
      }
    },

    // 读取当前选中对象的属性，填充到面板
    loadSelectedProperties() {
      if (!this.canvas) return;
      const obj = this.canvas.getActiveObject();
      if (!obj) return;

      // 仅在单选时显示属性面板，避免多选时混乱
      const activeCount = this.canvas.getActiveObjects().length;
      this.isSingleSelected = activeCount === 1;

      this.selectedType = obj.type || "";
      // 填充色（文本对象不显示填充色输入）
      this.selectedFill = typeof obj.fill === "string" ? obj.fill : "#000000";
      // 边框
      this.selectedStroke =
        typeof obj.stroke === "string" ? obj.stroke : "#000000";
      this.selectedStrokeWidth = obj.strokeWidth || 0;

      // 判断是否是文本对象
      this.isTextSelected =
        obj.type === "i-text" ||
        obj.type === "text" ||
        obj.type === "textbox";

      if (this.isTextSelected) {
        this.selectedText = obj.text || "";
        this.selectedFontSize = obj.fontSize || 24;
        this.selectedTextColor =
          typeof obj.fill === "string" ? obj.fill : "#000000";
      }
    },

    // 临时禁用历史记录（修改属性时不要每次输入都入栈）
    applyChange(mutator) {
      if (!this.canvas) return;
      const obj = this.canvas.getActiveObject();
      if (!obj) return;

      const wasUndoRedoing = this.isUndoRedoing;
      this.isUndoRedoing = true;
      try {
        mutator(obj);
        obj.setCoords && obj.setCoords();
        this.canvas.requestRenderAll();
      } finally {
        this.isUndoRedoing = wasUndoRedoing;
      }
    },

    // 应用填充颜色
    applyFillColor() {
      if (!this.isTextSelected) {
        this.applyChange((obj) => {
          obj.set("fill", this.selectedFill);
        });
      }
    },
    // 应用边框颜色
    applyStrokeColor() {
      this.applyChange((obj) => {
        obj.set("stroke", this.selectedStroke);
      });
    },
    // 应用边框宽度
    applyStrokeWidth() {
      this.applyChange((obj) => {
        obj.set("strokeWidth", Number(this.selectedStrokeWidth) || 0);
      });
    },
    // 应用文字内容
    applyText() {
      if (!this.isTextSelected) return;
      this.applyChange((obj) => {
        obj.set("text", this.selectedText);
      });
    },
    // 应用字体大小
    applyFontSize() {
      if (!this.isTextSelected) return;
      this.applyChange((obj) => {
        obj.set("fontSize", Number(this.selectedFontSize) || 24);
      });
    },
    // 应用文字颜色
    applyTextColor() {
      if (!this.isTextSelected) return;
      this.applyChange((obj) => {
        obj.set("fill", this.selectedTextColor);
      });
    },
  },
};
</script>

<style scoped>
.app-container {
  background-color: #f8fafc;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  padding: 10px !important;
}

.header {
  text-align: center;
  padding: 2rem 1rem;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
}

.title {
  font-size: 2.5rem;
  font-weight: bold;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.title-icon {
  color: #3b82f6;
  margin-right: 0.5rem;
}

.subtitle {
  color: #6b7280;
  font-size: 1.125rem;
}

.main-content {
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 2rem;
  margin: 0 auto;
}

.sidebar {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
}

.panel {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 10px;
}

.tool-section {
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  margin-bottom: 0.5rem;
}

.tool-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
}

.tool-btn {
  transition: all 0.3s ease;
}

.canvas-area {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.canvas-panel {
  /* height: 100%; */
  display: flex;
  flex-direction: column;
}

.canvas-container {
  flex: 1;
  padding: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f9fafb;
}

.canvas-container canvas {
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: white;
}

.polygon-info {
  margin-top: 0.5rem;
  padding: 0.5rem;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.full-width {
  width: 100%;
}

.margin-bottom-2 {
  margin-bottom: 0.5rem;
}

.margin-top-2 {
  margin-top: 0.5rem;
}

.mr-2 {
  margin-right: 0.5rem;
}

.flex-container {
  display: flex;
}

.gap-2 {
  gap: 0.5rem;
}

.color-picker {
  width: 48px;
  height: 32px;
  border-radius: 4px;
}

.range-slider {
  flex: 1;
}

.info-text {
  font-size: 0.875rem;
  color: #6b7280;
}

.file-input {
  display: none;
}

.custom-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.375rem;
  font-weight: 500;
  transition: all 0.2s;
  cursor: pointer;
  border: none;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  text-decoration: none;
  outline: 2px solid transparent;
  outline-offset: 2px;
}

.custom-btn-sm {
  padding: 0.7rem 0.75rem;
  font-size: 0.75rem;
  line-height: 1rem;
}

.custom-btn-primary {
  background-color: #3b82f6;
  color: white;
}

.custom-btn-primary:hover {
  background-color: #2563eb;
}

.custom-btn-secondary {
  background-color: #6b7280;
  color: white;
}

.custom-btn-secondary:hover {
  background-color: #4b5563;
}

.custom-btn-success {
  background-color: #10b981;
  color: white;
}

.custom-btn-success:hover {
  background-color: #059669;
}

.custom-btn-warning {
  background-color: #f59e0b;
  color: white;
}

.custom-btn-warning:hover {
  background-color: #d97706;
}

.custom-btn-error {
  background-color: #ef4444;
  color: white;
}

.custom-btn-error:hover {
  background-color: #dc2626;
}

.custom-btn-info {
  background-color: #06b6d4;
  color: white;
}

.custom-btn-info:hover {
  background-color: #0891b2;
}

.custom-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.custom-btn-warning:disabled {
  background-color: #f59e0b;
}

.custom-btn-primary:disabled {
  background-color: #3b82f6;
}

.custom-btn-secondary:disabled {
  background-color: #6b7280;
}

.custom-btn-success:disabled {
  background-color: #10b981;
}

.custom-btn-error:disabled {
  background-color: #ef4444;
}

.custom-btn-info:disabled {
  background-color: #06b6d4;
}

/* 房间列表样式 - 横向布局 */
.room-list-panel {
  margin-bottom: 16px;
  border: 1px solid #e6e6e6;
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.room-list-container {
  padding: 12px;
}

.room-list.horizontal {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 8px;
}

.room-item {
  flex: 0 0 auto;
  width: 200px;
  background-color: #f9fafb;
  border-radius: 6px;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
}

.room-item:hover {
  background-color: #f3f4f6;
  border-color: #d1d5db;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.room-name {
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.room-details {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.25rem;
}

.room-type {
  font-size: 0.75rem;
  color: #6b7280;
  background-color: #e5e7eb;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
}

.room-status {
  font-size: 0.75rem;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  font-weight: 500;
}

.room-extra {
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: #6b7280;
}

.room-area {
  font-weight: 500;
}

.room-owner {
  color: #4b5563;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100px;
}

.no-rooms {
  text-align: center;
  padding: 1.5rem;
  color: #6b7280;
}

.no-rooms i {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  display: block;
}

.no-rooms p {
  margin: 0;
  font-size: 0.875rem;
}

/* 选中对象属性面板样式 */
.prop-row {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  gap: 0.5rem;
}
.prop-label {
  flex: 0 0 70px;
  font-size: 0.75rem;
  color: #4b5563;
  text-align: right;
}
.prop-value {
  flex: 0 0 28px;
  text-align: center;
  font-size: 0.75rem;
  color: #6b7280;
  background: #f3f4f6;
  border-radius: 4px;
  padding: 2px 4px;
}
.text-input {
  flex: 1;
  min-width: 0;
  padding: 0.3rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.75rem;
  outline: none;
  transition: border-color 0.2s;
}
.text-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}
.selected-type-tag {
  display: inline-block;
  margin-left: 4px;
  padding: 1px 6px;
  font-size: 0.7rem;
  color: #1e40af;
  background: #dbeafe;
  border-radius: 8px;
  font-weight: 500;
}

/* 房间状态样式 */
.status-rented {
  background-color: #fee2e2;
  color: #b91c1c;
}

.status-available {
  background-color: #dcfce7;
  color: #166534;
}

.status-maintenance {
  background-color: #fef3c7;
  color: #92400e;
}

.status-reserved {
  background-color: #dbeafe;
  color: #1d4ed8;
}

.status-unsold {
  background-color: #f3e8ff;
  color: #7c3aed;
}

.status-delivered {
  background-color: #e0f2fe;
  color: #0369a1;
}

.status-unoccupied {
  background-color: #fefce8;
  color: #a16207;
}

.status-decorated {
  background-color: #ecfdf5;
  color: #047857;
}

.status-sold {
  background-color: #fee2e2;
  color: #b91c1c;
}

.status-vacant {
  background-color: #f0f9ff;
  color: #0284c7;
}

.status-decorating {
  background-color: #fef3c7;
  color: #92400e;
}

.status-unknown {
  background-color: #f3f4f6;
  color: #4b5563;
}

@media (max-width: 1024px) {
  .main-content {
    grid-template-columns: 1fr;
  }

  .sidebar {
    order: 2;
  }

  .canvas-area {
    order: 1;
  }
}
</style>
